import type { ReactNode } from 'react'
import {
  referralReceivingNotice,
  useReferralAccess,
  useReferralSummary,
} from '@entities/referrals/referrals'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { Button, Card, ErrorState, MoneyValue, Skeleton } from '@shared/design-system/ui'
import { dashboardGroup, headerCopy, kpiGrid, sectionSubtitle, sectionTitle } from './dashboard.css'

export function ReferralBalanceCard({ receivingNotice }: { receivingNotice?: ReactNode }) {
  const featureEnabled = useFeatureFlag('referralProgram')
  const access = useReferralAccess()
  const enabled =
    featureEnabled &&
    access.data?.enabled === true &&
    access.data.dashboardEnabled &&
    !access.isError
  const query = useReferralSummary(enabled)
  if (!enabled) return null
  return (
    <Card
      className={dashboardGroup}
      data-testid="dashboard-referral-balance"
      role="region"
      aria-label="Indique e ganhe"
    >
      <div className={headerCopy}>
        <h2 className={sectionTitle}>Indique e ganhe</h2>
        <p className={sectionSubtitle}>
          Ganhos acumulados e situação dos recebíveis de indicações, independentemente do período
          das vendas.
        </p>
      </div>
      {query.isError ? (
        <ErrorState
          title="Não foi possível atualizar suas indicações"
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <Skeleton height={100} />
      ) : (
        <>
          <div className={kpiGrid}>
            {[
              ['Comissões geradas', query.data.earnings.gross],
              ['Estornos', query.data.earnings.reversed],
              ['Ganhos líquidos', query.data.earnings.net],
              ['Liberação confirmada', query.data.balance.available],
              ['A liberar', query.data.balance.pending],
              ['Reservado', query.data.balance.reserve],
              ['Em processamento', query.data.balance.processing],
              ['Aguardando repasse', query.data.earnings.pending],
            ].map(([label, amount]) => (
              <div key={label} className={headerCopy}>
                <p className={sectionSubtitle}>{label}</p>
                <MoneyValue amount={amount!} currency="BRL" size="lg" />
              </div>
            ))}
          </div>
          <div className={headerCopy} data-testid="dashboard-referral-receiving-guidance">
            <p className={sectionSubtitle}>{referralReceivingNotice}</p>
            <p className={sectionSubtitle}>
              Ganhos líquidos já descontam estornos. Aguardando repasse ainda não compõe os
              recebíveis. Valores a liberar, reservados e em processamento já fazem parte de
              Recebimentos e não devem ser somados novamente. Liberação confirmada não confirma
              depósito bancário.
            </p>
          </div>
          {receivingNotice}
          <div>
            <Button
              variant="secondary"
              data-testid="dashboard-referrals-refresh-btn"
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              Atualizar indicações
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
